import classNames from "classnames";
import TalaeraSpinner from "components/TalaeraSpinner/TalaeraSpinner";
import {
  MouseEvent,
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { getErrorComponents } from "services/util";
import { trapTabKey } from "utils/a11y";
import { useDialogTracking } from "./analytics";
import { DialogProvider } from "./DialogContext";
import { DefaultDialogHeader } from "./headers";
import styles from "./index.module.scss";

type DialogProps = {
  open: boolean;
  /**
   * Names the dialog for assistive tech, and is what the header renders. A
   * string, not a node: an accessible name has to be announceable, and all 119
   * call sites already pass one.
   */
  title: string;
  onClose: () => void;
  /**
   * The panel's header. Defaults to DefaultDialogHeader; pass CenteredDialogHeader
   * or BasicDialogHeader for the other shapes, or null for a body that owns the
   * whole composition. A custom one reads the title and close handler from
   * DialogContext rather than taking them as props.
   */
  header?: ReactNode | null;
  // Body inset: "default" (20/22) fits forms, "roomy" (32/48, 24 on mobile) fits
  // celebration and prompt compositions, "none" hands the inset to a body that
  // brings its own.
  bodyPadding?: "default" | "roomy" | "none";
  /** The confirming action. Rightmost on a wide footer, top of the stacked column. */
  mainAction?: ReactNode;
  /** The dismissing action, placed against mainAction. */
  secondaryAction?: ReactNode;
  /**
   * Anything the footer needs beyond the confirm/dismiss pair, anchored to its far
   * left. The one slot the Dialog makes no assumptions about: rendered in the order
   * given, so a caller passing several owns their arrangement.
   */
  extraActions?: ReactNode;
  /**
   * Renders the kit inline spinner (.dialog-inline-spinner + TalaeraSpinner)
   * after the children. Purely visual — it disables nothing; pair with
   * disabled props on the footer actions where needed.
   */
  loading?: boolean;
  /**
   * When truthy, renders getErrorComponents(error) at the top of the body in
   * a polite live region. Accepts everything getErrorComponents does: API
   * error responses, ApiRequestError, strings, ReactNodes. Falsy skips.
   */
  error?: unknown;
  size?: "sm" | "md" | "lg" | "xl";
  /**
   * Opts the dialog into product analytics under this id: a stable snake_case
   * name for the dialog itself. Not derived from the title, which is translated
   * and in places interpolates the user's own name.
   */
  analyticsId?: string;
  /** Set false for dialogs with unsaved input — disables overlay-click AND Escape. Defaults to true. */
  closeOnOverlayClick?: boolean;
  /**
   * Visually hides the dialog while keeping it (and its children) mounted.
   * Transition-era affordance: legacy in-tree dialogs paint below a portaled
   * kit dialog, so a kit dialog that opens a legacy follow-on suspends itself
   * while the follow-on is up — imperative refs into its children stay live.
   */
  suspended?: boolean;
  /**
   * Extra class on the panel — the styling hook for call-site css (calendar
   * skins, per-dialog layout). Keep panel-level styling here, not in the kit.
   */
  className?: string;
  children: ReactNode;
};

/**
 * The app's dialog shell (rounded panel on a soft scrim): role="dialog",
 * aria-modal, labelled by its title, Escape-to-close, body scroll lock,
 * focus trap, and focus restore to the opener on close.
 *
 * The portal carries the .admin-v2 scope class so admin markup rendered inside
 * a dialog body still picks up the admin stylesheets; it is inert elsewhere.
 */
export default function Dialog(props: DialogProps) {
  const { open, onClose, closeOnOverlayClick } = props;
  const panelRef = useRef<HTMLDivElement>(null);
  const header = props.header === undefined ? <DefaultDialogHeader /> : props.header;

  const recordClose = useDialogTracking({
    dialog: props.analyticsId,
    open,
    hasError: Boolean(props.error),
  });

  // The chrome's own close, handed to the header through context so a custom
  // header gets the same wiring — and the same attribution.
  const closeFromChrome = useCallback(() => {
    recordClose("close_button");
    onClose();
  }, [recordClose, onClose]);

  useEffect(() => {
    if (!open || closeOnOverlayClick === false) {
      return;
    }
    const handleKeyDown = (evt: globalThis.KeyboardEvent) => {
      if (evt.key === "Escape") {
        recordClose("escape");
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose, closeOnOverlayClick, recordClose]);

  // Lock body scroll while the dialog is open — scrolling inside the dialog
  // must not scroll the page behind it.
  useEffect(() => {
    if (!open) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  // Move focus into the panel on open; hand it back to the opener on close.
  useEffect(() => {
    if (!open) {
      return;
    }
    const opener = document.activeElement;
    panelRef.current?.focus();
    return () => {
      if (opener instanceof HTMLElement && opener.isConnected) {
        opener.focus();
      }
    };
  }, [open]);

  if (!open) {
    return null;
  }

  const handleOverlayClick = (evt: MouseEvent) => {
    if (evt.target === evt.currentTarget && props.closeOnOverlayClick !== false) {
      recordClose("overlay");
      onClose();
    }
  };

  // Keep Tab cycling inside the panel while the dialog is open.
  const handleTrapKeyDown = (evt: ReactKeyboardEvent) => {
    if (evt.key === "Tab") {
      trapTabKey(panelRef.current, evt);
    }
  };

  return createPortal(
    <DialogProvider title={props.title} onClose={closeFromChrome}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- backdrop click-to-close supplements Escape; the overlay is deliberately not in the tab order. */}
      <div
        className={classNames(
          "admin-v2",
          styles.overlay,
          props.suspended && styles.suspended
        )}
        aria-hidden={props.suspended || undefined}
        onClick={handleOverlayClick}
      >
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the keydown IS the focus trap; role="dialog" containers are the one place a div legitimately listens for Tab. */}
        <div
          ref={panelRef}
          tabIndex={-1}
          className={classNames(
            styles.dialog,
            props.size === "sm" && styles.sm,
            props.size === "lg" && styles.lg,
            props.size === "xl" && styles.xl,
            props.className
          )}
          role="dialog"
          aria-modal="true"
          aria-label={props.title}
          onKeyDown={handleTrapKeyDown}
        >
          {header}

          <div
            className={classNames(
              styles.body,
              props.bodyPadding === "roomy" && styles.bodyRoomy,
              props.bodyPadding === "none" && styles.bodyNone
            )}
          >
            {Boolean(props.error) && (
              <div role="status" aria-live="polite">
                {getErrorComponents(props.error)}
              </div>
            )}
            {props.children}
            {props.loading && (
              <div className="dialog-inline-spinner">
                <TalaeraSpinner />
              </div>
            )}
          </div>

          {(props.mainAction || props.secondaryAction || props.extraActions) && (
            <footer className={styles.foot}>
              {props.extraActions && (
                <div className={styles.footExtra}>{props.extraActions}</div>
              )}
              {props.secondaryAction}
              {props.mainAction}
            </footer>
          )}
        </div>
      </div>
    </DialogProvider>,
    document.body
  );
}

type StickyActionsProps = {
  className?: string;
  children: ReactNode;
};

/**
 * Pins primary actions to the bottom of the dialog body's scrollport, so tall
 * body content scrolls under them instead of pushing them out of view. Use this
 * for wizard-style dialogs whose CTA lives in the scrolling body rather than
 * the Dialog `footer` prop.
 *
 * Place as a direct child of the dialog body, or of a container spanning the
 * body's full scroll height: position:sticky cannot escape its parent.
 */
export function StickyActions(props: StickyActionsProps) {
  return (
    <div className={classNames(styles.stickyActions, props.className)}>
      {props.children}
    </div>
  );
}
